<template>
  <div class="bitacora-admin">
    <v-container fluid grid-list-xl>
      <v-layout row wrap>
        <!-- Hero -->
        <v-flex xs12>
          <div class="list-hero">
            <p class="list-crumb">Admin / Bitácoras</p>
            <h1 class="list-title">Plantillas de Bitácora</h1>
            <p class="list-subtitle">
              Defina los flujos de pasos que siguen los transportistas y
              mensajeros. La plantilla por defecto de cada tipo se copia a cada
              envío nuevo.
            </p>
          </div>
        </v-flex>

        <v-flex xs12>
          <v-card class="panel">
            <!-- Toolbar -->
            <v-card-text class="panel__toolbar">
              <v-layout row wrap align-center>
                <v-flex md8 sm7 xs12>
                  <v-text-field
                    v-model="search"
                    clearable
                    flat
                    hide-details
                    solo
                    class="panel-search"
                    placeholder="Buscar plantilla..."
                    append-icon="search"
                  ></v-text-field>
                </v-flex>
                <v-flex md4 sm5 xs12 class="toolbar-actions">
                  <v-btn color="#e5252a" dark class="btn-nuevo" @click="goNew">
                    <v-icon left>add</v-icon>Nueva Plantilla
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-card-text>

            <v-progress-linear
              v-if="loading"
              indeterminate
              color="#254F87"
              height="3"
              class="ma-0"
            ></v-progress-linear>

            <!-- Desktop table -->
            <v-card-text class="pa-0 hidden-sm-and-down">
              <div class="table-wrap">
                <table class="bita-table" cellspacing="0">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Tipo</th>
                      <th>Default</th>
                      <th>Estado</th>
                      <th>Pasos</th>
                      <th class="th-actions">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="t in filteredTemplates" :key="t.id">
                      <td class="cell-name">{{ t.name }}</td>
                      <td>
                        <span :class="typeClass(t.assignmentType)">{{
                          typeLabel(t.assignmentType)
                        }}</span>
                      </td>
                      <td>
                        <span v-if="t.isDefault" class="default-badge">
                          <v-icon x-small>star</v-icon> Default
                        </span>
                        <span v-else class="muted">—</span>
                      </td>
                      <td>
                        <span
                          :class="
                            t.enabled
                              ? 'status-chip status-chip--on'
                              : 'status-chip status-chip--off'
                          "
                          >{{ t.enabled ? 'Activa' : 'Inactiva' }}</span
                        >
                      </td>
                      <td>{{ (t.steps && t.steps.length) || 0 }}</td>
                      <td>
                        <div class="action-group">
                          <v-btn
                            class="btn-edit"
                            icon
                            small
                            title="Editar"
                            @click="goEdit(t)"
                          >
                            <v-icon small>edit</v-icon>
                          </v-btn>
                          <v-btn
                            class="btn-delete"
                            icon
                            small
                            title="Eliminar"
                            @click="openDelete(t)"
                          >
                            <v-icon small>delete</v-icon>
                          </v-btn>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="!loading && filteredTemplates.length === 0">
                      <td colspan="6" class="empty-row">
                        No hay plantillas registradas.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-card-text>

            <!-- Mobile cards -->
            <v-card-text class="hidden-md-and-up">
              <div
                v-if="!loading && filteredTemplates.length === 0"
                class="empty-row"
              >
                No hay plantillas registradas.
              </div>
              <div
                v-for="t in filteredTemplates"
                :key="`m-${t.id}`"
                class="m-card"
              >
                <div class="m-card__head">
                  <p class="m-card__name">{{ t.name }}</p>
                  <span
                    :class="
                      t.enabled
                        ? 'status-chip status-chip--on'
                        : 'status-chip status-chip--off'
                    "
                    >{{ t.enabled ? 'Activa' : 'Inactiva' }}</span
                  >
                </div>
                <div class="m-card__row">
                  <span class="m-label">Tipo</span>
                  <span :class="typeClass(t.assignmentType)">{{
                    typeLabel(t.assignmentType)
                  }}</span>
                </div>
                <div class="m-card__row">
                  <span class="m-label">Default</span>
                  <span v-if="t.isDefault" class="default-badge"
                    ><v-icon x-small>star</v-icon> Default</span
                  >
                  <span v-else class="muted">—</span>
                </div>
                <div class="m-card__row">
                  <span class="m-label">Pasos</span>
                  <span>{{ (t.steps && t.steps.length) || 0 }}</span>
                </div>
                <div class="m-card__actions">
                  <v-btn class="btn-edit" small icon @click="goEdit(t)">
                    <v-icon small>edit</v-icon>
                  </v-btn>
                  <v-btn class="btn-delete" small icon @click="openDelete(t)">
                    <v-icon small>delete</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>

    <delete-confirm-dialog
      v-model="deleteDialog.show"
      :max-width="460"
      title="Eliminar plantilla"
      subtitle="Esta acción no se puede deshacer"
      :message="`¿Eliminar la plantilla ${deleteDialog.name}?`"
      warning="No afecta envíos ya creados (los pasos se copian al crear el envío)."
      confirm-text="Sí, eliminar"
      @confirm="deleteTemplate"
    ></delete-confirm-dialog>

    <v-snackbar
      v-model="snack.show"
      :timeout="3500"
      :color="snack.color"
      right
      top
    >
      {{ snack.message }}
      <v-btn flat dark @click="snack.show = false">Cerrar</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import Api from '@/api/index'
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog'

function extractList(resp) {
  if (!resp) return []
  if (Array.isArray(resp)) return resp
  if (Array.isArray(resp.items)) return resp.items
  if (resp.data) {
    if (Array.isArray(resp.data)) return resp.data
    if (Array.isArray(resp.data.items)) return resp.data.items
  }
  return []
}

export default {
  name: 'BitacoraTemplates',
  components: { DeleteConfirmDialog },
  data() {
    return {
      templates: [],
      search: '',
      loading: false,
      deleteDialog: { show: false, id: null, name: 'esta plantilla' },
      snack: { show: false, color: 'info', message: '' },
    }
  },
  computed: {
    filteredTemplates() {
      const term = (this.search || '').trim().toLowerCase()
      if (!term) return this.templates
      return this.templates.filter(t =>
        (t.name || '').toLowerCase().includes(term)
      )
    },
  },
  created() {
    this.loadTemplates()
  },
  methods: {
    notify(message, color = 'info') {
      this.snack = { show: true, color, message }
    },
    isHttpError(res) {
      return !!(res && typeof res.status === 'number' && res.status >= 400)
    },
    typeLabel(type) {
      if (type === 'mensajero') return 'Mensajero'
      if (type === 'transportista') return 'Transportista'
      return 'Ambos'
    },
    typeClass(type) {
      if (type === 'mensajero') return 'type-chip type-chip--mensajero'
      if (type === 'transportista') return 'type-chip type-chip--transportista'
      return 'type-chip type-chip--all'
    },
    async loadTemplates() {
      this.loading = true
      try {
        const res = await Api.BitacoraTemplate.getAll()
        this.templates = extractList(res)
      } catch (e) {
        this.notify('Error al cargar las plantillas', 'error')
      } finally {
        this.loading = false
      }
    },
    goNew() {
      this.$router.push('/admin/bitacoras/nuevo')
    },
    goEdit(template) {
      this.$router.push(`/admin/bitacoras/editar/${template.id}`)
    },
    openDelete(template) {
      this.deleteDialog = {
        show: true,
        id: template.id,
        name: template.name || 'esta plantilla',
      }
    },
    async deleteTemplate() {
      const id = this.deleteDialog.id
      if (id == null) return
      this.deleteDialog.show = false
      try {
        const res = await Api.BitacoraTemplate.delete(id)
        if (this.isHttpError(res)) throw new Error('http')
        this.notify('Plantilla eliminada', 'success')
        this.loadTemplates()
      } catch (e) {
        this.notify('No se pudo eliminar la plantilla', 'error')
      }
    },
  },
}
</script>

<style scoped>
.bitacora-admin {
  background: #f8fafc;
  min-height: 100vh;
  padding-bottom: 16px;
}

/* Hero */
.list-hero {
  padding: 4px 0 8px;
}
.list-crumb {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}
.list-title {
  margin: 4px 0;
  font-size: 32px;
  line-height: 1.1;
  color: #0f172a;
  font-weight: 700;
}
.list-subtitle {
  margin: 0;
  font-size: 14px;
  color: #475569;
  max-width: 760px;
}

/* Panel */
.panel {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  background: #fff;
}
.panel__toolbar {
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
}
.toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}
.btn-nuevo {
  border-radius: 8px;
  text-transform: none;
  font-weight: 600;
}

/* Table */
.table-wrap {
  overflow-x: auto;
}
.bita-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.bita-table th {
  padding: 11px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}
.bita-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  vertical-align: middle;
}
.bita-table tbody tr:hover {
  background: #f8fafc;
}
.cell-name {
  font-weight: 600;
  color: #172133;
}
.th-actions {
  text-align: center;
}
.muted {
  color: #94a3b8;
}

.type-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 700;
  white-space: nowrap;
}
.type-chip--transportista {
  background: #e8eef9;
  color: #2f4d7a;
}
.type-chip--mensajero {
  background: #ffedd5;
  color: #c2410c;
}
.type-chip--all {
  background: #f1f5f9;
  color: #475569;
}

.default-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 700;
  background: #fef3c7;
  color: #92711b;
}
.default-badge .v-icon {
  color: #d9a40a !important;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 700;
}
.status-chip--on {
  background: #dcfce7;
  color: #15803d;
}
.status-chip--off {
  background: #fee2e2;
  color: #b91c1c;
}

.action-group {
  display: flex;
  gap: 8px;
}
.btn-edit {
  border: 1px solid rgba(37, 79, 135, 0.3) !important;
  color: #254f87 !important;
  background: rgba(37, 79, 135, 0.1) !important;
  box-shadow: none !important;
}
.btn-delete {
  border: 1px solid rgba(229, 37, 42, 0.3) !important;
  color: #e5252a !important;
  background: rgba(229, 37, 42, 0.1) !important;
  box-shadow: none !important;
}

.empty-row {
  text-align: center;
  color: #94a3b8;
  padding: 24px 16px;
  font-size: 13px;
}

/* Mobile cards */
.m-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 10px;
  background: #fff;
}
.m-card__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.m-card__name {
  margin: 0;
  font-weight: 700;
  color: #172133;
}
.m-card__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  font-size: 13px;
}
.m-label {
  color: #64748b;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
}
.m-card__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}
</style>
